import { useMemo, useState } from "react";

let cachedCountryMap: Record<string, string> | null = null;

// Build country name → ISO code map once
const createCountryNameToCodeMap = (): Record<string, string> => {
  const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
  const map: Record<string, string> = {};

  for (let i = 65; i <= 90; i++) {
    for (let j = 65; j <= 90; j++) {
      const code = String.fromCharCode(i) + String.fromCharCode(j);
      const name = regionNames.of(code);
      if (name) map[name.toLowerCase()] = code.toLowerCase();
    }
  }

  return map;
};

// Lookup function (cached)
const getCountryCodeFromName = (countryName: string): string | null => {
  if (!cachedCountryMap) cachedCountryMap = createCountryNameToCodeMap();
  return cachedCountryMap[countryName.toLowerCase()] || null;
};

// ---- Hook ----
export const useGooglePlaceAutocomplete = (
  defaultCountryName: string = "Australia"
) => {
  const [suggestions, setSuggestions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [loading, setLoading] = useState(false);

  const countryCode = useMemo(() => {
    return getCountryCodeFromName(defaultCountryName);
  }, [defaultCountryName]);

  const fetchSuggestions = (query: string) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);

    const autocompleteService = new google.maps.places.AutocompleteService();

    const request: google.maps.places.AutocompletionRequest = {
      input: query,
      ...(countryCode && {
        componentRestrictions: { country: countryCode },
      }),
    };

    autocompleteService.getPlacePredictions(
      request,
      (
        predictions: google.maps.places.AutocompletePrediction[] | null,
        status: google.maps.places.PlacesServiceStatus
      ) => {
        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          setSuggestions(predictions);
        } else {
          setSuggestions([]);
        }
        setLoading(false);
      }
    );
  };

  return { suggestions, loading, fetchSuggestions };
};
